import { Box, Button, MenuItem, Select, TextField } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import {
  type Application,
  type Role,
  type RoleAssignmentRequest,
  admin,
  applications,
  bulkUsers,
} from "../../data/api";
import { useAction, useLoad } from "../../data/data";
import {
  Failure,
  Field,
  FormRow,
  Hint,
  Mono,
  PageHeader,
  Section,
} from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import type { TenantPageProps } from "../pages";

// ---
// Adding one person to a tenant
//
// Its own page rather than a panel over the table: a list of assignments that grows, plus how the
// person is onboarded, is a form with its own shape — and as a place it can be linked to,
// survives a reload, and makes Back mean cancel.
//
// One route does all of it. `POST /admin/users/bulk` creates the identity, issues the invitation
// and makes every assignment in one transaction — a row carries a list, so a person may hold the
// same role twice narrowed to different projects without a second call.
// ---

/**
 * One assignment as the form holds it.
 *
 * `project: ""` rather than absent, because a Select needs a value for "no project chosen" and
 * `undefined` makes it uncontrolled. Converted on the way out, where absent means tenant-wide.
 */
interface DraftAssignment {
  readonly roleKey: string;
  readonly project: string;
}

const EMPTY: DraftAssignment = { roleKey: "", project: "" };

const asRequest = ({ roleKey, project }: DraftAssignment): RoleAssignmentRequest => ({
  roleKey,
  ...(project === "" ? {} : { project }),
});

export function NewUser({ tenant, token }: TenantPageProps): React.JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const roles = useLoad(() => admin.roles(tenant, token), [tenant, token]);
  // The tenant's projects, not the caller's. `TenantPageProps.projects` answers "which projects
  // can I mint a data-plane token for", which is empty for a pure administrator — a different
  // question from "which project may this assignment be confined to".
  const inTenant = useLoad(() => admin.projects(tenant, token), [tenant, token]);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [drafts, setDrafts] = useState<DraftAssignment[]>([EMPTY]);
  const [sent, setSent] = useState<{ email: string; url: string } | null>(null);
  // Where the invitation lands. Only applications that have registered a return URL can be
  // offered: without one there is nowhere to send anybody.
  const apps = useLoad(() => applications.list(tenant, token), [tenant, token]);
  const [destination, setDestination] = useState("");
  // Two, because the difference between the buttons is only what happens after it worked: one
  // stays here with an empty form, the other goes back to the list.
  const again = useAction();
  const andBack = useAction(() => navigate({ to: "/tenants/$tenant/users", params: { tenant } }));
  const busy = again.busy || andBack.busy;

  const assignable = new Set(roles.data?.delegable ?? []);
  const humanRoles = (roles.data?.roles ?? []).filter(
    (role: Role) => role.actorType === "human" && assignable.has(role.key),
  );
  const chosen = drafts.filter((draft) => draft.roleKey !== "");
  const withReturnUrls = (apps.data ?? []).filter(
    (app: Application) => app.status === "active" && app.redirectUris.length > 0,
  );
  const ready = email !== "" && username !== "" && chosen.length > 0;

  const edit = (index: number, change: Partial<DraftAssignment>) =>
    setDrafts((all) => all.map((draft, at) => (at === index ? { ...draft, ...change } : draft)));

  const clear = () => {
    setEmail("");
    setUsername("");
    setDrafts([EMPTY]);
  };

  /** Creates the identity, its assignments and the invitation, in one job of one row. */
  const create = () => async () => {
    const answer = await bulkUsers(
      tenant,
      token,
      [{ email, username, assignments: chosen.map(asRequest) }],
      false,
      destination === "" ? undefined : { continueTo: destination },
    );
    const row = answer.results[0];
    if (row?.status === "error") throw new Error(row.detail ?? "Could not add this user");
    setSent({ email, url: row?.inviteUrl ?? "" });
    clear();
  };

  return (
    <>
      <PageHeader title="New user" />

      {sent !== null && (
        <Section title="Invitation sent">
          <Box sx={{ typography: "body1", color: tokens.ink.secondary }}>
            {sent.email} has been added and mailed a link, if a transport is configured. It is shown
            once here so it can be delivered by hand otherwise.
          </Box>
          {sent.url !== "" && (
            <Box sx={{ mt: 3 }}>
              <Mono sx={{ wordBreak: "break-all" }}>{sent.url}</Mono>
            </Box>
          )}
        </Section>
      )}

      <Section title="Identity">
        <Hint sx={{ mt: 0, mb: 4 }}>
          One identity, used in every tenant. An email already in use belongs to someone who exists
          — add them from the Users table instead, which keeps their credential.
        </Hint>
        <FormRow>
          <Field label="Email" sx={{ flex: "1 1 320px", maxWidth: 420 }}>
            <TextField
              size="small"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="someone@example.com"
            />
          </Field>
        </FormRow>
        <FormRow sx={{ mt: 3 }}>
          <Field label="Username" sx={{ flex: "1 1 320px", maxWidth: 420 }}>
            <TextField
              size="small"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="someone"
            />
          </Field>
        </FormRow>
      </Section>

      <Section title="Access">
        <Hint sx={{ mt: 0, mb: 4 }}>
          Tenant-wide covers every project, including ones created later. The same role may be added
          twice, narrowed to different projects. Only roles you may delegate are offered.
        </Hint>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {drafts.map((draft, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a row is identified by its position
            <FormRow key={index}>
              <Field label={index === 0 ? "Project" : ""} sx={{ flex: "0 1 200px" }}>
                <Select
                  size="small"
                  value={draft.project}
                  displayEmpty
                  onChange={(event) => edit(index, { project: event.target.value })}
                >
                  <MenuItem value="">Tenant-wide</MenuItem>
                  {(inTenant.data ?? []).map((each) => (
                    <MenuItem key={each.id} value={each.id}>
                      {each.id}
                    </MenuItem>
                  ))}
                </Select>
              </Field>
              <Field label={index === 0 ? "Role" : ""} sx={{ flex: "1 1 260px", maxWidth: 320 }}>
                <Select
                  size="small"
                  value={draft.roleKey}
                  displayEmpty
                  onChange={(event) => edit(index, { roleKey: event.target.value })}
                >
                  <MenuItem value="" disabled>
                    {humanRoles.length === 0 ? "No delegable roles" : "Choose a role"}
                  </MenuItem>
                  {humanRoles.map((role: Role) => (
                    <MenuItem key={role.key} value={role.key}>
                      {role.key}
                    </MenuItem>
                  ))}
                </Select>
              </Field>
              <Button
                variant="text"
                aria-label="Remove this role"
                disabled={drafts.length === 1}
                onClick={() => setDrafts((all) => all.filter((_, at) => at !== index))}
                sx={{ minWidth: 0, px: 2 }}
              >
                <X size={15} />
              </Button>
            </FormRow>
          ))}
        </Box>
        <Button
          variant="text"
          startIcon={<Plus size={15} />}
          sx={{ mt: 3 }}
          onClick={() => setDrafts((all) => [...all, EMPTY])}
        >
          Add another role
        </Button>
      </Section>

      <Section title="Onboarding">
        <Box sx={{ typography: "body1", color: tokens.ink.secondary }}>
          They receive a single-use link and set their own password. The account is created without
          a usable one until they do, so no administrator ever handles someone else's credential.
        </Box>
        <FormRow sx={{ mt: 4 }}>
          <Field label="Send them to" sx={{ flex: "1 1 320px", maxWidth: 440 }}>
            <Select
              size="small"
              value={destination}
              displayEmpty
              onChange={(event) => setDestination(event.target.value)}
            >
              <MenuItem value="">This console</MenuItem>
              {withReturnUrls.map((app) => (
                <MenuItem key={app.clientId} value={app.redirectUris[0]}>
                  {app.name}
                </MenuItem>
              ))}
            </Select>
          </Field>
        </FormRow>
        <Hint>
          {withReturnUrls.length === 0
            ? "No application has a return URL yet, so the link can only land here. Add one on the Applications page."
            : "The link lands here by default. Choose an application to finish onboarding inside it instead."}
        </Hint>
        <Hint>
          Setting an initial password instead is not available: no route accepts a password for
          another subject. See <Mono>docs/users-page-design.md</Mono>.
        </Hint>
      </Section>

      <Failure error={again.error ?? andBack.error} />

      <FormRow sx={{ mt: 5, justifyContent: "flex-end" }}>
        <Button
          variant="text"
          onClick={() => void navigate({ to: "/tenants/$tenant/users", params: { tenant } })}
        >
          Cancel
        </Button>
        <Button variant="outlined" disabled={!ready || busy} onClick={() => again.run(create())}>
          Create another
        </Button>
        <Button variant="contained" disabled={!ready || busy} onClick={() => andBack.run(create())}>
          Send invitation
        </Button>
      </FormRow>
    </>
  );
}
